/**
 * Stripe Service for Chartara Notes
 * Handles subscription management and payment processing
 */

import { loadStripe } from '@stripe/stripe-js';

class StripeService {
  constructor() {
    this.stripePromise = null;
    this.apiBaseUrl = process.env.REACT_APP_API_GATEWAY_URL || '/api';
    this.initializeStripe();
  }

  /**
   * Initialize Stripe with publishable key
   */
  initializeStripe() {
    const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 
                     window.CHARTARA_CONFIG?.STRIPE_PUBLISHABLE_KEY;
    
    if (stripeKey) {
      this.stripePromise = loadStripe(stripeKey);
    } else {
      console.warn('Stripe publishable key not configured');
    }
  }

  /**
   * Get Stripe instance
   */
  async getStripe() {
    if (!this.stripePromise) {
      throw new Error('Stripe not initialized - missing publishable key');
    }
    return await this.stripePromise;
  }

  /**
   * Create checkout session for subscription
   * @param {string} priceId - Stripe price ID
   * @param {string} userEmail - User's email
   * @param {string} userId - User's ID
   * @returns {Object} Checkout session response
   */
  async createCheckoutSession(priceId, userEmail, userId) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/stripe-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId,
          userEmail,
          userId,
          successUrl: `${window.location.origin}/subscription/success`,
          cancelUrl: `${window.location.origin}/subscription/plans`
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Redirect to Stripe Checkout
   * @param {string} priceId - Stripe price ID
   * @param {string} userEmail - User's email
   * @param {string} userId - User's ID
   */
  async redirectToCheckout(priceId, userEmail, userId) {
    try {
      const stripe = await this.getStripe();
      const { sessionId } = await this.createCheckoutSession(priceId, userEmail, userId);

      const { error } = await stripe.redirectToCheckout({
        sessionId: sessionId
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
      throw error;
    }
  }

  /**
   * Get user's current subscription status
   * @param {string} userId - User's ID
   * @returns {Object} Subscription details
   */
  async getSubscriptionStatus(userId) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/stripe-status/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting subscription status:', error);
      return { 
        status: 'none',
        plan: 'trial',
        daysRemaining: 14,
        error: error.message 
      };
    }
  }

  /**
   * Cancel subscription
   * @param {string} subscriptionId - Stripe subscription ID
   */
  async cancelSubscription(subscriptionId) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/stripe-cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ subscriptionId })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  /**
   * Create customer portal session
   * @param {string} customerId - Stripe customer ID
   * @returns {Object} Portal session URL
   */
  async createPortalSession(customerId) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/stripe-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          customerId,
          returnUrl: `${window.location.origin}/dashboard`
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  }

  /**
   * Subscription plan configurations
   */
  getSubscriptionPlans() {
    return {
      individual_monthly: {
        id: 'individual_monthly',
        name: 'Free Plan',
        price: '$0',
        interval: 'month',
        features: [
          'AI-powered clinical documentation',
          'Voice-to-note in seconds',
          'Visual analytics & insights dashboard',
          'ML-powered clinical decision support',
          'Simple web forms — no spreadsheets',
          'Custom note templates',
          'HIPAA-compliant & secure'
        ],
        priceId: process.env.REACT_APP_STRIPE_INDIVIDUAL_MONTHLY_PRICE_ID
      },
      individual_annual: {
        id: 'individual_annual',
        name: 'Individual Annual',
        price: null,
        interval: 'year',
        monthlyEquivalent: null,
        savings: null,
        features: [
          'AI-powered clinical documentation',
          'Voice-to-note in seconds',
          'Visual analytics & insights dashboard',
          'ML-powered clinical decision support',
          'Simple web forms — no spreadsheets',
          'Custom note templates',
          'HIPAA-compliant & secure',
          'Priority support'
        ],
        priceId: process.env.REACT_APP_STRIPE_INDIVIDUAL_ANNUAL_PRICE_ID
      },
      practice: {
        id: 'practice',
        name: 'Supervisor Plan',
        price: '$15 / seat / month',
        interval: 'month',
        features: [
          'A complete AI documentation infrastructure for your entire practice',
          'Every clinician gets the full AI scribe, custom templates & analytics',
          'Monitor every clinician’s caseload analytics from one dashboard',
          'Drill into any clinician to target and improve supervision',
          'Practice-wide visual analytics across your whole team',
          'Add or remove seats anytime'
        ]
      }
    };
  }

  /**
   * Check if user is in trial period
   * @param {Object} subscription - Subscription object
   * @returns {boolean} True if in trial
   */
  isInTrial(subscription) {
    if (!subscription || subscription.status === 'none') {
      return true; // New users get trial
    }
    return subscription.status === 'trialing';
  }

  /**
   * Get days remaining in trial
   * @param {Object} subscription - Subscription object
   * @returns {number} Days remaining
   */
  getTrialDaysRemaining(subscription) {
    if (!subscription || !subscription.trialEnd) {
      return 14; // Default trial period
    }
    
    const trialEnd = new Date(subscription.trialEnd);
    const now = new Date();
    const diffTime = trialEnd - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  }
}

// Create singleton instance
export const stripeService = new StripeService();
export default stripeService;